<template>
  <v-container>
    <h1 class="text-center">🖥️ Meine Server</h1>
    <p class="text-center text-body-2">
      Wähle aus, für welchen Discord-Server du den Bot gerade konfigurieren möchtest.
    </p>

    <v-alert v-if="!loading && guilds.length === 0" type="info" outlined class="mb-4">
      Es ist noch kein Discord-Server verbunden.
    </v-alert>

    <v-alert v-if="!loading && guilds.length > 1" type="info" dense outlined class="mb-4 text-left">
      Du hast mehrere Discord-Server verbunden. Der <strong>Community-Server</strong> (⭐) ist
      der Server, für den der Rollen-Sync (Sub/VIP-Rollen) sowie die Twitch-Discord-Verknüpfung
      deiner Zuschauer aktiv ist — es kann immer nur einer sein.
    </v-alert>

    <v-list two-line>
      <v-list-item-group v-model="selectedIndex" color="primary">
        <v-list-item
          v-for="(guild, index) in guilds"
          :key="guild.id"
          :disabled="switching"
          @click="select(guild, index)"
        >
          <v-list-item-avatar v-if="guild.icon">
            <v-img :src="guild.icon"></v-img>
          </v-list-item-avatar>
          <v-list-item-icon v-else>
            <v-icon>mdi-discord</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>
              {{ guild.name || guild.id }}
              <v-icon v-if="guild.isCommunity" color="amber darken-2" small class="ml-1">mdi-star</v-icon>
            </v-list-item-title>
            <v-list-item-subtitle v-if="guild.isCommunity" class="amber--text text--darken-3">
              Community-Server (Rollen-Sync aktiv)
            </v-list-item-subtitle>
            <v-list-item-subtitle v-if="!guild.reachable" class="red--text">
              Bot ist auf diesem Server nicht (mehr) erreichbar
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action v-if="guilds.length > 1 && !guild.isCommunity">
            <v-btn text small :loading="settingCommunity === guild.id" @click.stop="makeCommunity(guild)">
              Als Community setzen
            </v-btn>
          </v-list-item-action>
          <v-list-item-action v-if="guild.id === selectedGuildId">
            <v-chip color="primary" small>Aktiv</v-chip>
          </v-list-item-action>
          <v-list-item-action>
            <v-btn icon color="red" :disabled="switching" @click.stop="confirmRemove(guild)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list-item-group>
    </v-list>

    <v-dialog v-model="removeDialog" max-width="500">
      <v-card>
        <v-card-title>Server entfernen?</v-card-title>
        <v-card-text>
          <p>
            Möchtest du <strong>{{ (guildToRemove && (guildToRemove.name || guildToRemove.id)) || '' }}</strong>
            wirklich entfernen?
          </p>
          <p class="mb-0">
            Dabei werden <strong>alle</strong> Einstellungen für diesen Server unwiderruflich gelöscht
            (Live-Ankündigungen, Ticket-System, Regelwerk, Rollen-Sync, Temp. Sprachkanäle, ...).
            Die dadurch freiwerdende Lizenz kannst du danach für einen anderen Server verwenden.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="removeDialog = false">Abbrechen</v-btn>
          <v-btn color="red" text :loading="removing" @click="removeConfirmed">Entfernen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row class="mt-4">
      <v-col cols="12" class="text-center">
        <v-alert v-if="!loading && !hasAvailableDiscordLicense" type="warning" outlined dense class="mb-4">
          Keine freie Discord-Server-Lizenz — bitte Support kontaktieren, um eine weitere zu erhalten.
        </v-alert>
        <v-alert v-if="!loading && hasAvailableDiscordLicense" type="info" dense outlined class="mb-2 text-left">
          Der neue Server wird für den Twitch-Kanal <strong>{{ currentInstanz || 'Unbekannt' }}</strong>
          verbunden. Falls das nicht stimmt, wechsle zuerst über den Login die Botinstanz.
        </v-alert>
        <v-checkbox
          v-if="!loading && hasAvailableDiscordLicense"
          v-model="instanceConfirmed"
          :label="`Ja, der Server soll mit '${currentInstanz || 'Unbekannt'}' verbunden werden.`"
          dense
          hide-details
          class="mb-2 justify-center"
        ></v-checkbox>
        <v-btn
          color="primary"
          :block="$vuetify.breakpoint.smAndDown"
          :disabled="!hasAvailableDiscordLicense || !instanceConfirmed"
          :href="instanceConfirmed ? inviteUrl : undefined"
          @click="onInviteClick"
        >
          <v-icon left>mdi-plus</v-icon>
          Weiteren Server hinzufügen
        </v-btn>
      </v-col>
    </v-row>

    <v-snackbar
      v-model="switchResult.show"
      timeout="3000"
      :color="switchResult.success ? 'green' : 'red'"
    >
      {{ switchResult.message }}
    </v-snackbar>
  </v-container>
</template>

<script>

import BackendService from '@/services/BackendService';

export default {
  data() {
    return {
      loading: true,
      switching: false,
      guilds: [],
      selectedGuildId: null,
      selectedIndex: null,
      hasAvailableDiscordLicense: false,
      instanceConfirmed: false,
      settingCommunity: null,
      removeDialog: false,
      guildToRemove: null,
      removing: false,
      switchResult: {
        show: false,
        success: false,
        message: '',
      },
    };
  },
  computed: {
    currentInstanz() {
      return this.$store.state.instanz;
    },
    inviteUrl() {
      const clientId = process.env.VUE_APP_DISCORD_BOT_CLIENT_ID;
      const redirectUri = process.env.VUE_APP_DISCORD_BOT_INVITE_REDIRECT_URL;
      const permissions = '286649360';
      const scope = encodeURIComponent('bot applications.commands');
      return `https://discord.com/oauth2/authorize?client_id=${clientId}&scope=${scope}&permissions=${permissions}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&state=my_servers`;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    onInviteClick() {
      if (this.instanceConfirmed) {
        sessionStorage.setItem('dcInviteConfirmedInstanz', this.currentInstanz || '');
      }
    },
    load() {
      this.loading = true;
      BackendService.listDiscordGuilds().then((data) => {
        this.guilds = data.data.guilds;
        this.selectedGuildId = data.data.selectedGuildId;
        this.hasAvailableDiscordLicense = data.data.hasAvailableDiscordLicense;
        this.selectedIndex = this.guilds.findIndex((g) => g.id === this.selectedGuildId);
      }).finally(() => {
        this.loading = false;
      });
    },
    select(guild) {
      if (guild.id === this.selectedGuildId) return;
      this.switching = true;
      BackendService.selectGuild({ guildId: guild.id }).then(() => {
        this.selectedGuildId = guild.id;
        this.switchResult = { show: true, success: true, message: `${guild.name || guild.id} ist jetzt aktiv.` };
      }).catch(() => {
        this.switchResult = { show: true, success: false, message: 'Wechsel fehlgeschlagen.' };
        this.selectedIndex = this.guilds.findIndex((g) => g.id === this.selectedGuildId);
      }).finally(() => {
        this.switching = false;
      });
    },
    makeCommunity(guild) {
      this.settingCommunity = guild.id;
      BackendService.setCommunityGuild({ guildId: guild.id }).then(() => {
        this.switchResult = { show: true, success: true, message: `${guild.name || guild.id} ist jetzt der Community-Server.` };
        this.load();
      }).catch(() => {
        this.switchResult = { show: true, success: false, message: 'Umstellen des Community-Servers fehlgeschlagen.' };
      }).finally(() => {
        this.settingCommunity = null;
      });
    },
    confirmRemove(guild) {
      this.guildToRemove = guild;
      this.removeDialog = true;
    },
    removeConfirmed() {
      if (!this.guildToRemove) return;
      this.removing = true;
      BackendService.disconnectDiscordGuild({ guildId: this.guildToRemove.id }).then(() => {
        this.switchResult = { show: true, success: true, message: 'Server wurde entfernt, Lizenz ist wieder frei.' };
        this.removeDialog = false;
        this.load();
      }).catch(() => {
        this.switchResult = { show: true, success: false, message: 'Entfernen fehlgeschlagen.' };
      }).finally(() => {
        this.removing = false;
      });
    },
  },
};
</script>
